/**
 * Lot Renderer Service
 *
 * Builds the `lotRenderer` adapter object that wires site-plan lot domain logic
 * into the generic `svgOverlays` facade via the `handlers.lotRenderer` slot.
 *
 * This is the only file that imports from both the lot domain (sitePlanLots,
 * lotPolygons) and references the facade contract shape. The library layer
 * (svgOverlays.js) stays free of lot-specific imports.
 *
 * @module searchResultsMap/services/lotRenderer
 */

import {
  buildLotStatusIndex,
  sortLayersByOrder,
} from './sitePlanLots';
import renderLotStatusClasses from '../components/lotPolygons';

/**
 * Builds the `lotRenderer` object that `svgOverlays.js` calls for all lot-specific
 * rendering.
 *
 * @param {Object} lotClasses  - CSS class map { inactive, active, sold, unreleased }
 * @returns {Object} lotRenderer — passed into handlers.lotRenderer
 */
export default function buildLotRenderer(lotClasses) {
  const lotClassValues = Object.values(lotClasses);
  return {
    /**
     * Builds a lotId→lot status index from raw lot data.
     * Called on attach and on syncData.
     *
     * @returns {{ lotStatusIndex: Map }}
     */
    buildLotData(sitePlanLots) {
      return {
        lotStatusIndex: buildLotStatusIndex(sitePlanLots),
      };
    },

    /** Sorts layers by their `order` field before group creation. */
    sortLayers: sortLayersByOrder,

    /** Applies/removes status classes on all lot polygon paths after data or zoom changes. */
    applyStatusClasses(svgRoot, lotStatusIndex) {
      renderLotStatusClasses(svgRoot, lotStatusIndex, lotClasses, lotClassValues);
    },
  };
}
